import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { canAccessFinancial, canAccessSupportRecords } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import SECURITY_QUESTIONS from "@/constants/securityQuestions";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard,
  CalendarDays,
  UtensilsCrossed,
  ClipboardList,
  MapPin,
  Wrench,
  ShieldCheck,
  Sparkles,
  LogOut,
  Menu,
  X,
  Lock,
  ContactRound,
  UserRound,
  ClipboardPlus,
  Bell,
  Wallet,
} from "lucide-react";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_dorm-attendance-4/artifacts/c3wpw9lm_images%20%288%29.jpeg";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ old_password: "", new_password: "", confirm_password: "", security_answer: "", security_question: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const isVisitor = user?.role === "visitor" || user?.staff_role === "Visitor";
  const nav = isVisitor
    ? [
        { to: "/student-outing", label: "Student Outing", icon: ClipboardList, testid: "nav-student-outing" },
        { to: "/incidents", label: "Feedback", icon: Wrench, testid: "nav-incidents" },
      ]
    : [
        { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/my-profile", label: "My Profile", icon: UserRound, testid: "nav-my-profile" },
        { to: "/attendance", label: "Attendance", icon: MapPin, testid: "nav-attendance" },
        { to: "/events", label: "Event & Calendar", icon: CalendarDays, testid: "nav-events" },
        { to: "/student-outing", label: "Student Outing", icon: ClipboardList, testid: "nav-student-outing" },
        { to: "/org-chart", label: "Hostel Directory", icon: ContactRound, testid: "nav-org-chart" },
        { to: "/hostel-innovation", label: "Hostel Innovation & Achievement", icon: Sparkles, testid: "nav-hostel-innovation" },
        { to: "/incidents", label: "Feedback", icon: Wrench, testid: "nav-incidents" },
        { to: "/dewan-makan", label: "Refectory & Meals", icon: UtensilsCrossed, testid: "nav-dewan-makan" },
      ];
  if (!isVisitor && canAccessSupportRecords(user))
    nav.splice(5, 0, { to: "/student-support-records", label: "Student Record", icon: ClipboardPlus, testid: "nav-student-support-records" });
  if (canAccessFinancial(user))
    nav.push({ to: "/financial", label: "Financial", icon: Wallet, testid: "nav-financial" });
  if (user?.role === "admin")
    nav.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" });

  useEffect(() => {
    if (isVisitor || !user) return;

    const loadNotifications = async () => {
      try {
        const response = await api.get("/notifications");
        setNotifications(response.data || []);
      } catch (error) {
        setNotifications([]);
      }
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [isVisitor, user]);

  const unreadCount = notifications.filter((item) => !item.read).length;

  const handleReadAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update notifications");
    }
  };

  const handleClearNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications((current) => current.filter((notification) => notification.id !== notificationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to clear notification");
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await api.delete("/notifications");
      setNotifications([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to clear notifications");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password || !passwordForm.security_answer.trim()) {
      toast.error("Please complete all password fields");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
        security_answer: passwordForm.security_answer,
        security_question: passwordForm.security_question || undefined,
      });
      toast.success("Password updated successfully");
      setPasswordDialogOpen(false);
      setPasswordForm({ old_password: "", new_password: "", confirm_password: "", security_answer: "", security_question: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 shrink-0">
            <img src={LOGO} alt="Logo SMKMI" className="h-10 w-10 rounded-full object-cover bg-white" />
            <div className="leading-tight">
              <p className="font-display font-extrabold text-sm sm:text-base tracking-tight leading-tight">Asrama SMKMI</p>
              <p className="text-[11px] text-muted-foreground -mt-0.5">Sistem Pengurusan Asrama</p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 flex-1 min-w-0 overflow-x-auto mx-2">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                {initials}
              </div>
              <div className="leading-tight text-right">
                <p className="text-sm font-semibold max-w-[140px] truncate">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPasswordDialogOpen(true)}
              className="hidden sm:inline-flex"
              data-testid="change-password-button"
            >
              <Lock className="h-4 w-4 mr-1" /> Password
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {!isVisitor && (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications((value) => !value)}
                  aria-label="Notifications"
                  className="relative"
                  data-testid="notification-bell-button"
                >
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>

                {showNotifications && (
                  <div className="absolute right-0 top-12 z-50 w-[min(22rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-popover p-2 shadow-xl">
                    <div className="flex items-center justify-between gap-2 border-b border-border px-2 pb-2">
                      <p className="text-sm font-semibold">Notifications</p>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button type="button" className="text-xs text-primary hover:underline" onClick={handleReadAll}>Mark all read</button>
                        )}
                        {notifications.length > 0 && (
                          <button type="button" className="text-xs text-destructive hover:underline" onClick={handleClearAllNotifications}>Clear all</button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-[min(70vh,30rem)] overflow-y-auto py-2">
                      {notifications.length === 0 ? (
                        <p className="px-2 py-4 text-sm text-muted-foreground">No notifications yet.</p>
                      ) : (
                        notifications.map((item) => (
                          <div key={item.id} className={`flex w-full gap-2 rounded-lg px-3 py-2 transition-colors ${item.read ? "bg-transparent hover:bg-secondary/60" : "bg-primary/5 hover:bg-primary/10"}`}>
                            <button
                              type="button"
                              className="min-w-0 flex-1 text-left"
                              onClick={async () => {
                                if (!item.read) {
                                  try {
                                    await api.post(`/notifications/${item.id}/read`);
                                    setNotifications((current) => current.map((notification) => notification.id === item.id ? { ...notification, read: true } : notification));
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || "Unable to mark notification as read");
                                  }
                                }
                              }}
                            >
                              <p className="text-sm font-medium break-words">{item.title}</p>
                              <p className="mt-1 text-xs text-muted-foreground whitespace-normal break-words overflow-wrap-anywhere">{item.message}</p>
                            </button>
                            <button
                              type="button"
                              className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                              aria-label="Clear notification"
                              onClick={() => handleClearNotification(item.id)}
                            >
                              Clear
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {open && (
          <nav className="md:hidden border-t border-border px-4 py-2 space-y-1">
            {nav.map((n) => {
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary"
                >
                  <Icon className="h-4 w-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Verify your current password and security answer before choosing a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current password</Label>
              <PasswordInput value={passwordForm.old_password} onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })} placeholder="Current password" />
            </div>
            {user?.security_question ? (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">{user.security_question}</div>
            ) : (
              <div className="space-y-2">
                <Label>Choose a security question</Label>
                <Select value={passwordForm.security_question} onValueChange={(security_question) => setPasswordForm({ ...passwordForm, security_question })}>
                  <SelectTrigger><SelectValue placeholder="Choose a question" /></SelectTrigger>
                  <SelectContent>{SECURITY_QUESTIONS.map((question) => <SelectItem key={question} value={question}>{question}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Security answer</Label>
              <PasswordInput value={passwordForm.security_answer} onChange={(e) => setPasswordForm({ ...passwordForm, security_answer: e.target.value })} placeholder="Your answer" autoComplete="off" />
            </div>
            <div className="space-y-2">
              <Label>New password</Label>
              <PasswordInput value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} placeholder="New password" />
            </div>
            <div className="space-y-2">
              <Label>Confirm new password</Label>
              <PasswordInput value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} placeholder="Confirm new password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePasswordChange} disabled={changingPassword}>{changingPassword ? "Updating…" : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-up">
        {children}
      </main>
    </div>
  );
};

export default Layout;
